import {
  Box,
  chakra,
  Button as ChakraButton,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
} from '@chakra-ui/react';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AccessLinkTypes, AdminMasterClassListViewQuery } from '@/@generated/codegen/API';
import { AccessLinkPanelClient } from '@/components/AccessLinkPanel/AccessLinkPanelClient';
import { usePaginationState } from '@/hooks/usePaginationState';
import { useTableQueryParamsUtils } from '@/hooks/useTableQueryParamsUtils';
import ColumnVisibility from '@/ui/TanstackTable/Columns/ColumnVisibility';
import ActiveFilters from '@/ui/TanstackTable/Filter/ActiveFilters/ActiveFilters';
import Filters, { FilterExtraInfo } from '@/ui/TanstackTable/Filter/Filters';
import GlobalFilter from '@/ui/TanstackTable/Filter/GlobalFilter/GlobalFilter';
import { FilterType } from '@/ui/TanstackTable/Filter/NewFilterComponent';
import Pagination from '@/ui/TanstackTable/Pagination';
import TanstackTableView from '@/ui/TanstackTable/TanstackTableView';
import { fuzzyFilter } from '@/utils/helpers/fuzzyFilter';

import useAdminMasterClassTableStore from './AdminMasterclassListTableStore';
import getColumns, { AdminMasterClassListItemType } from './columns';
import { CreateMasterClassForm } from '../CreateMasterClassForm/CreateMasterClassForm';

type Props = {
  data: AdminMasterClassListViewQuery;
  pagination: ReturnType<typeof usePaginationState>;
  tablePrefix: string;
};

const filterExtraInfos: FilterExtraInfo<AdminMasterClassListItemType> = [
  {
    filterId: 'clients',
    filterType: FilterType.STRING,
    queryParamKey: 'clientName',
  },
];

export function AdminMasterclassListView({ data, pagination, tablePrefix }: Props) {
  const { t } = useTranslation();
  const totalCount = data.listMasterClasses.totalCount;
  const tableData: AdminMasterClassListItemType[] = useMemo(() => {
    const masterClassList = data.listMasterClasses.edges.map((edge) => edge.node);
    return masterClassList;
  }, [data.listMasterClasses]);
  const [showMasterClassForm, setShowMasterClassForm] = useState<boolean>(false);
  const [isDupingMasterClass, setIsDupingMasterClass] = useState<boolean>(false);
  const [masterClassData, setMasterClassData] = useState<AdminMasterClassListItemType | undefined>(
    undefined
  );
  const [managedMasterClassID, setManagedMasterClassID] = useState<string | undefined>(undefined);
  // const managedMasterClass = tableData.find((m) => m.id === managedMasterClassID)!;
  // const [showAccessFormMasterClass, setShowAccessFormMasterClass] = useState<boolean>(false);
  const [showAccessLinkPanel, setShowAccessLinkPanel] = useState<boolean>(false);
  const {
    columnFilters,
    sorting,
    globalFilter,
    onColumnFiltersChange,
    onSortingChange,
    onGlobalFilterChange,
  } = useTableQueryParamsUtils(filterExtraInfos, tablePrefix);
  const [columnVisibility, setColumnVisibility, columnSizing, setColumnSizing] =
    useAdminMasterClassTableStore((state) => [
      state.columnVisibility,
      state.setColumnVisibility,
      state.columnSizing,
      state.setColumnSizing,
    ]);
  // const handleDupeMasterClass = (row: AdminMasterClassListItemType) => {
  //   setShowMasterClassForm(true);
  //   setMasterClassData(row);
  //   setIsDupingMasterClass(true);
  // };

  // const handleEditMasterClass = (row: AdminMasterClassListItemType) => {
  //   setShowMasterClassForm(true);
  //   setMasterClassData(row);
  // };
  const columns = getColumns({
    t,
    onAccessLinkClick: (masterClass: AdminMasterClassListItemType) => {
      setManagedMasterClassID(masterClass.id);
      setShowAccessLinkPanel(true);
    },
    onPreviewClick: (id: string) => {
      alert(
        'TODO: navigate to masterclass preview page (removed this since masterclass migration)'
      );
    },
    // onViewCaseClick: (el) => {
    //   navigate(`${ROUTES.PREVIEW_CASE}/${el.id}?${QUERY_PARAM_PREVIEW_CASE_TYPE}=masterClass`);
    // },
  });

  const table = useReactTable({
    data: tableData,
    manualPagination: true,
    pageCount: Math.ceil(totalCount / pagination.paginationState.pageSize),
    state: {
      pagination: pagination.paginationState,
      columnSizing,
      columnVisibility,
      sorting,
    },
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    onColumnVisibilityChange: (updater) => {
      const newVisibility = typeof updater === 'function' ? updater(columnVisibility) : updater;
      setColumnVisibility(newVisibility);
    },
    onColumnSizingChange: (updater) => {
      const newSizing = typeof updater === 'function' ? updater(columnSizing) : updater;
      setColumnSizing(newSizing);
    },
    onPaginationChange: pagination.createOnPaginationChangeFunc(data.listMasterClasses.pageInfo),
    onGlobalFilterChange: onGlobalFilterChange,
    onSortingChange: onSortingChange,
    defaultColumn: {},
    columnResizeMode: 'onEnd',
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columns: columns,
  });

  // --------------------- handlers ------------------------

  const handleCloseForm = useCallback(() => {
    setMasterClassData(undefined);
    setIsDupingMasterClass(false);
    setShowMasterClassForm(false);
  }, []);

  // const handleOnShowArchivedChange = useCallback(
  //   (e: ChangeEvent<HTMLInputElement>) => {
  //     const newValue = e.currentTarget.checked;
  //     const newFilters = columnFilters.filter((el) => el.id !== 'isArchived');

  //     newFilters.push({
  //       id: 'isArchived',
  //       value: newValue,
  //     });
  //     onColumnFiltersChange(newFilters);
  //   },
  //   [columnFilters]
  // );

  const titleLabel = useMemo(() => {
    if (masterClassData?.title) {
      if (isDupingMasterClass) {
        return t('admin.masterClass.button.duplicateMasterClass', {
          masterClassName: masterClassData.title,
        });
      }
      return t('admin.masterClass.button.updateMasterClass', {
        masterClassName: masterClassData.title,
      });
    }
    return t('admin.masterClass.button.createnewMasterClass');
  }, [masterClassData, isDupingMasterClass]);

  // --------------------- render ------------------------

  return (
    <Box maxW="full" p={{ base: 3, md: 3 }}>
      {/* TODO: add the user access panel here */}
      {/* {managedMasterClass && showAccessFormMasterClass && (
        <MasterClassUserAccessList
          masterClass={managedMasterClass}
          onClose={() => {
            setShowAccessFormMasterClass(false);
          }}
        />
      )} */}
      {showAccessLinkPanel && managedMasterClassID && (
        <AccessLinkPanelClient
          type={AccessLinkTypes.MasterclassReadAccess}
          objId={managedMasterClassID}
          onClose={() => {
            setManagedMasterClassID(undefined);
            setShowAccessLinkPanel(false);
          }}
        />
      )}
      <Flex justifyContent="space-between" wrap="wrap" gap={2} alignItems="center" my={2}>
        <chakra.h3 fontSize="2xl" fontWeight="bold">
          MasterClasses
        </chakra.h3>

        <ChakraButton
          background="blue.400"
          color="white"
          size={{ base: 'sm', md: 'md' }}
          data-test="create-masterClass-button-popUp"
          onClick={() => setShowMasterClassForm(true)}
        >
          {t('admin.masterClass.button.createnewMasterClass')}
        </ChakraButton>

        <Modal isOpen={showMasterClassForm} onClose={() => handleCloseForm()}>
          <ModalOverlay />
          <ModalContent w="full" maxW={{ base: '95vw', md: '400px' }}>
            <ModalCloseButton />
            <ModalBody w="full">
              <Flex flexDir="column" w="full" p="20px" gap="10px">
                <Heading size="sm" pb="5">
                  {titleLabel}
                </Heading>

                <CreateMasterClassForm
                  masterClass={masterClassData}
                  isDuplicating={isDupingMasterClass}
                  closeForm={handleCloseForm}
                />
              </Flex>
            </ModalBody>
          </ModalContent>
        </Modal>
      </Flex>

      {data && (
        <>
          <Flex wrap="wrap" gap={2} alignItems="center" mb="4">
            <ColumnVisibility table={table} />
            <Filters
              columnFilters={columnFilters}
              hiddenFilters={[]}
              setColumnFilters={onColumnFiltersChange}
              table={table}
              filterExtraInfo={filterExtraInfos}
            />
            <GlobalFilter
              globalFilter={globalFilter}
              setGlobalFilter={onGlobalFilterChange}
              maxWidth={{ base: 'none', md: '250px' }}
              flex={{ base: 1, md: 'initial' }}
              minW="150px"
            />
            <ActiveFilters
              columnFilters={columnFilters}
              hiddenFilters={[]}
              removeItemFunc={(elem) => {
                onColumnFiltersChange(columnFilters.filter((f) => f.id !== elem.id));
              }}
              filterExtraInfo={filterExtraInfos}
            />
            <Pagination
              table={table}
              pageInfo={data.listMasterClasses.pageInfo}
              totalRows={totalCount}
              tablePrefix={tablePrefix}
              ml="auto"
            />
          </Flex>

          <TanstackTableView table={table} />
        </>
      )}
    </Box>
  );
}
